import React from 'react'
import { css } from '@emotion/core'
import { useCallback, useEffect, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { number, string } from 'prop-types'
import { useMediaPredicate } from 'react-media-hook'
import { useTheme } from 'emotion-theming'

// Internal components
import { Link, Button } from '@jelly/ui'
import { ArrowNavigation } from '../svgs/ArrowNavigation'
import { ArrowsNavigation } from '../svgs/ArrowsNavigation'

const StyledLink = styled(Link)`
	${({ theme, variant }) => theme.buttons[variant]};
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 1.25rem;
	${({ selected = false, theme }) =>
		selected &&
		css`
			background-color: ${theme.navigation.linkColor};
			color: ${theme.colors.white};
		`}
	${({ disabled = false, theme }) =>
		disabled &&
		css`
			background-color: ${theme.colors.gray[300]};
			cursor: not-allowed;
			transition: none;
		`}
`

const NavigationButton = ({ disabled, children, ...props }) => {
	return disabled ? <Button disabled>{children}</Button> : <StyledLink {...props}>{children}</StyledLink>
}

StyledLink.defaultProps = {
	variant: 'default',
}

const PaginationContainer = styled.div`
	margin-top: 1rem;
	margin-bottom: 5rem;
	display: flex;
	justify-content: center;
	flex-flow: nowrap;
	a,
	button {
		padding: 0px;
		margin: 0.2rem;
		line-height: ${({ buttonSize }) => buttonSize}px;
		width: ${({ buttonSize }) => buttonSize}px;
		height: ${({ buttonSize }) => buttonSize}px;
		text-align: center;
		font-size: ${({ showMobile }) => (showMobile ? '1' : '1.25')}rem;
	}
`
const DEFAULT_FIRST_PAGE = 1
const BUTTONS_TO_DISCOUNT = 5
/**
 * @todo: Look for options to store in the client the default value
 **/
const DESKTOP_ARRAY_LENGTH = 10
const MOBILE_ARRAY_LENGTH = 3
const DESKTOP_BUTTON_SIZE = 52
const MOBILE_BUTTON_SIZE = 42

Pagination.propTypes = {
	/** Current page number */
	page: number.isRequired,
	/** Path to create routes */
	path: string.isRequired,
	/** Last page to display in the buttons bar */
	lastPage: number.isRequired,
}

const generatePath = (path, page) => {
	return Number(page) === 1 ? path : `${path}${page}/`
}
/**
 * Pagination component for ResultList
 */
export function Pagination({ page, path, lastPage }) {
	/**
	 * arrayLength is going to define how many buttons we are displaying
	 * @type {number}
	 */
	const [arrayLength, setArrayLength] = useState(lastPage < DESKTOP_ARRAY_LENGTH ? lastPage : DESKTOP_ARRAY_LENGTH)
	const [buttonSize, setButtonSize] = useState(DESKTOP_BUTTON_SIZE)
	const currentPage = Number(page)
	const containerRef = useRef(null)
	const showMobile = !useMediaPredicate(useTheme().MATCH_MEDIA.M)
	const isFirstPage = currentPage === 1
	const isLastPage = currentPage === lastPage

	useEffect(() => {
		const resize = () => {
			const buttonSize = showMobile ? MOBILE_BUTTON_SIZE : DESKTOP_BUTTON_SIZE
			const defaultArrayLength = showMobile ? MOBILE_ARRAY_LENGTH : DESKTOP_ARRAY_LENGTH

			const clientWidth = containerRef.current && containerRef.current.offsetWidth
			const calculatedLenght = Math.floor(clientWidth / buttonSize) - BUTTONS_TO_DISCOUNT
			let arrayLength = Math.min(lastPage, calculatedLenght, defaultArrayLength)
			setButtonSize(buttonSize)
			setArrayLength(arrayLength)
		}

		resize()
		window.addEventListener('resize', resize)

		return () => {
			window.removeEventListener('resize', resize)
		}
	}, [lastPage, showMobile])

	const urlPathByPage = useCallback((page) => generatePath(path, page), [path])

	// If is the last page we modify the array length for buttons
	let initial =
		currentPage >= arrayLength
			? isLastPage
				? currentPage - (arrayLength - DEFAULT_FIRST_PAGE)
				: currentPage - (arrayLength - 2)
			: DEFAULT_FIRST_PAGE

	return (
		<PaginationContainer ref={containerRef} buttonSize={buttonSize} showMobile={showMobile}>
			<NavigationButton disabled={isFirstPage} href={urlPathByPage(DEFAULT_FIRST_PAGE)}>
				<ArrowsNavigation direction="first" role="img" aria-label="First" />
			</NavigationButton>
			<NavigationButton disabled={isFirstPage} href={urlPathByPage(currentPage - DEFAULT_FIRST_PAGE)}>
				<ArrowNavigation direction="previous" role="img" aria-label="Previous" />
			</NavigationButton>
			{Array.from({ length: arrayLength }, (v, index) => {
				const pageNumber = index + initial
				return (
					<StyledLink
						selected={pageNumber === currentPage}
						key={pageNumber}
						variant="transparent"
						href={generatePath(path, pageNumber)}
					>
						{pageNumber}
					</StyledLink>
				)
			})}
			<NavigationButton disabled={isLastPage} href={urlPathByPage(currentPage + DEFAULT_FIRST_PAGE)}>
				<ArrowNavigation direction="next" role="img" aria-label="Next" />
			</NavigationButton>
			<NavigationButton disabled={isLastPage} href={urlPathByPage(lastPage)}>
				<ArrowsNavigation direction="last" role="img" aria-label="Last" />
			</NavigationButton>
		</PaginationContainer>
	)
}
